'use client';

import { Popover, Transition } from '@headlessui/react';
import { animated, useTrail } from '@react-spring/web';
import Image from 'next/image';
import PropTypes from 'prop-types';
import React, { Fragment, useRef, useState } from 'react';
import { HiHeart } from 'react-icons/hi';
import {
  LikeType,
  LikeTypeDetails,
} from '../../constants/likeDetails.constant';
import Button from '../next-ui/Button/Button';
import Typography from '../next-ui/Typography';
import ReactionOptionButton from './ReactionOptionButton';

export default function ReactionButton(props) {
  const { onChange, userReaction, variant = 'icon-and-label' } = props;
  const buttonRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const timerRef = React.useRef(null);

  const timeoutDuration = 200;

  const reactionsTrail = useTrail(LikeTypeDetails.length, {
    opacity: hovering ? 1 : 0.6,
    transform: hovering ? 'translateY(0)' : 'translateY(24px)',
    config: { tension: 180, friction: 12 },
  });

  const { hasReaction, reactionType } = userReaction || {};
  const reaction = userReaction?.hasReaction
    ? LikeTypeDetails.find((l) => l.type === reactionType)
    : null;

  const closePopover = () => {
    setHovering(false);
    timerRef.current = null;
    return buttonRef.current?.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        bubbles: true,
        cancelable: true,
      }),
    );
  };

  const onMouseEnter = (open) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!open) {
      setHovering(true);
      buttonRef.current?.focus();
      buttonRef.current?.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Enter',
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  };

  const onMouseLeave = (open) => {
    if (open) {
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => closePopover(), timeoutDuration);
      }
    }
  };

  const handleReaction = (like) => {
    onChange(like.type);
    closePopover();
  };

  const handleToggleReaction = () => {
    if (hasReaction) {
      onChange(null);
    } else {
      onChange(LikeType.love);
    }
  };

  const renderReaction = () => {
    if (variant === 'label') {
      return (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ color: hasReaction && reaction.color }}>
          {hasReaction ? reaction.label : 'Like'}
        </Typography>
      );
    }
    return hasReaction ? (
      <>
        <Image
          width={12}
          height={12}
          src={reaction.icon}
          alt={reaction.label}
          className="h-3 w-3 object-contain object-center"
        />
        <Typography variant="h6" style={{ color: reaction.color }}>
          {reaction.label}
        </Typography>
      </>
    ) : (
      <>
        <HiHeart className="h-4 w-4 text-text-secondary" />
        <Typography variant="h6">Like</Typography>
      </>
    );
  };

  return (
    <Popover className="relative">
      {({ open }) => {
        return (
          <div onMouseLeave={onMouseLeave.bind(null, open)}>
            <Popover.Button
              ref={buttonRef}
              as={Button}
              variant="plain"
              size={variant === 'label' && 'xs'}
              onMouseEnter={onMouseEnter.bind(null, open)}
              onMouseLeave={onMouseLeave.bind(null, open)}
              onClick={handleToggleReaction}>
              <div className="flex items-center justify-center gap-4">
                {renderReaction()}
              </div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute bottom-full left-0 z-1000 h-10 w-60 -translate-x-6 transform px-2">
                {({ close }) => (
                  <div
                    className="overflow-visible rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                    onMouseEnter={onMouseEnter.bind(null, open)}
                    onMouseLeave={onMouseLeave.bind(null, open)}>
                    <div className="relative">
                      <div className="flex w-max flex-row gap-2 rounded-lg bg-white px-2 shadow-md transition-all duration-500">
                        {reactionsTrail.map((styles, index) => (
                          <animated.div
                            key={LikeTypeDetails[index].label}
                            className="relative"
                            style={styles}>
                            <ReactionOptionButton
                              label={LikeTypeDetails[index].label}
                              onClick={() => {
                                handleReaction(LikeTypeDetails[index]);
                                close();
                              }}>
                              <Image
                                width={32}
                                height={32}
                                src={LikeTypeDetails[index].icon}
                                alt={LikeTypeDetails[index].label}
                                className="h-8 w-8 object-contain object-center"
                              />
                            </ReactionOptionButton>
                          </animated.div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </div>
        );
      }}
    </Popover>
  );
}

ReactionButton.propTypes = {
  onChange: PropTypes.func.isRequired,
  userReaction: PropTypes.shape({
    hasReaction: PropTypes.bool.isRequired,
    reactionType: PropTypes.string,
    as: PropTypes.string,
  }),
  variant: PropTypes.oneOf(['icon-and-label', 'label']),
};
